import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, PencilSimple, Check, X, Lightning, FilePdf, TrendUp, ShareNetwork } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * CustomMetricsPanel — trainer manages templates (name + unit) and can log
 * values on behalf of any client; clients see active templates and log values
 * on themselves. Renders a history mini-chart + table per template.
 *
 * Props:
 *   clientId  (required)  the client whose entries we're viewing/logging against
 *   canEditTemplates      if true, template CRUD UI is shown (trainer-only)
 */
export default function CustomMetricsPanel({ clientId, canEditTemplates = false }) {
  const [templates, setTemplates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editDirection, setEditDirection] = useState("desc");
  const [logInputs, setLogInputs] = useState({}); // metric_id -> value

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, eRes] = await Promise.all([
        api.get("/custom-metrics"),
        clientId ? api.get(`/clients/${clientId}/custom-metric-entries`) : Promise.resolve({ data: [] }),
      ]);
      setTemplates(tRes.data || []);
      setEntries(eRes.data || []);
    } catch (err) {
      toast.error("Failed to load custom metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clientId]);

  const entriesByMetric = useMemo(() => {
    const map = {};
    for (const e of entries) {
      (map[e.metric_id] = map[e.metric_id] || []).push(e);
    }
    return map;
  }, [entries]);

  const createTemplate = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/custom-metrics", { name: newName.trim(), unit: newUnit.trim() || null });
      setNewName(""); setNewUnit(""); setShowNew(false);
      toast.success("Metric template added");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditUnit(t.unit || "");
    setEditDirection(t.direction || "desc");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.patch(`/custom-metrics/${editingId}`, { name: editName.trim(), unit: editUnit.trim() || null, direction: editDirection });
      setEditingId(null);
      toast.success("Saved");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Remove this metric? Existing entries will remain but the template will be deactivated.")) return;
    try {
      await api.delete(`/custom-metrics/${id}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const logValue = async (metric_id) => {
    const raw = logInputs[metric_id];
    if (raw === undefined || raw === "" || isNaN(Number(raw))) {
      toast.error("Enter a numeric value first");
      return;
    }
    try {
      await api.post(`/clients/${clientId}/custom-metric-entries`, { metric_id, value: Number(raw) });
      setLogInputs((prev) => ({ ...prev, [metric_id]: "" }));
      toast.success("Logged");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/custom-metric-entries/${id}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/custom-metrics/seed-defaults");
      const created = (data?.created || []).length;
      if (created > 0) {
        toast.success(`Added ${created} starter test${created > 1 ? "s" : ""} — you're ready to log.`);
      } else {
        toast("Starter tests already exist — nothing to add.");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed starter tests");
    }
  };

  const downloadPdf = async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/clients/${clientId}/assessment-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Could not generate PDF");
    }
  };

  const sharePr = async (metricId) => {
    if (!clientId) return;
    try {
      const res = await api.get(`/clients/${clientId}/metrics/${metricId}/pr-card`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "image/png" });
      const file = new File([blob], "pr.png", { type: "image/png" });
      // Try the Web Share API first (native mobile share sheet); fall back to download
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "New PR!", text: "Just hit a new personal best." });
          return;
        } catch (_e) { /* user cancelled — fall through to download */ }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "pr-card.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PR card saved — post it wherever you like.");
    } catch (err) {
      toast.error("Could not generate PR card");
    }
  };

  if (loading) return <div className="tracker text-zinc-500">Loading custom metrics…</div>;

  return (
    <div className="space-y-6" data-testid="custom-metrics-panel">
      {canEditTemplates && (
        <div className="card-flat p-4 sm:p-6" data-testid="custom-metric-templates">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h3 className="font-display text-xl">Assessment tests</h3>
              <p className="text-xs text-zinc-500 mt-1">Define once — every client can log results against these (e.g. &ldquo;500m bike&rdquo; · seconds).</p>
            </div>
            <div className="flex items-center gap-2">
              {templates.length === 0 && (
                <button onClick={seedDefaults} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid="seed-default-metrics-btn">
                  <Lightning size={12} weight="bold" /> Load starter protocol
                </button>
              )}
              <button onClick={() => setShowNew((v) => !v)} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="add-custom-metric-btn">
                <Plus size={12} weight="bold" /> New test
              </button>
            </div>
          </div>
          {templates.length === 0 && !showNew && (
            <div className="border border-dashed border-white/10 p-4 mb-4 text-center" data-testid="assessment-empty-hint">
              <p className="text-sm text-zinc-400 mb-2">No tests yet — start with the 5 classic benchmarks:</p>
              <p className="text-[11px] text-zinc-500 mb-3 font-mono">500m Bike · Dead Hang · Broad Jump · 40m Sprint · Vertical Jump</p>
              <button onClick={seedDefaults} className="btn-primary !py-1.5 !px-4 !text-[10px]">
                <Lightning size={12} weight="bold" /> Load starter protocol
              </button>
            </div>
          )}
          {showNew && (
            <div className="mb-4 grid sm:grid-cols-[2fr_1fr_auto_auto] gap-2 items-end">
              <div>
                <label className="tracker text-[10px] block mb-1">Name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. 500m bike" className="w-full px-3 py-2 border bg-transparent" data-testid="new-metric-name" />
              </div>
              <div>
                <label className="tracker text-[10px] block mb-1">Unit (optional)</label>
                <input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="seconds / kg / reps" className="w-full px-3 py-2 border bg-transparent" data-testid="new-metric-unit" />
              </div>
              <button onClick={createTemplate} className="btn-primary" data-testid="new-metric-save"><Check size={12} weight="bold" /> Add</button>
              <button onClick={() => { setShowNew(false); setNewName(""); setNewUnit(""); }} className="btn-ghost"><X size={12} weight="bold" /></button>
            </div>
          )}
          <div className="space-y-2">
            {templates.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-4">No templates yet.</div>
            ) : (
              templates.map((t) => (
                <div key={t.id} className="flex items-center gap-3 border-b border-white/5 py-2" data-testid={`metric-template-${t.id}`}>
                  {editingId === t.id ? (
                    <>
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 px-2 py-1 border bg-transparent text-sm" />
                      <input value={editUnit} onChange={(e) => setEditUnit(e.target.value)} className="w-24 px-2 py-1 border bg-transparent text-sm" placeholder="unit" />
                      <select
                        value={editDirection}
                        onChange={(e) => setEditDirection(e.target.value)}
                        className="px-2 py-1 border bg-transparent text-xs"
                        data-testid={`metric-template-direction-${t.id}`}
                      >
                        <option value="desc">Higher = better</option>
                        <option value="asc">Lower = better</option>
                      </select>
                      <button onClick={saveEdit} className="btn-primary !py-1 !px-2 !text-[10px]" data-testid={`metric-template-save-${t.id}`}><Check size={12} weight="bold" /></button>
                      <button onClick={() => setEditingId(null)} className="btn-ghost !py-1 !px-2 !text-[10px]"><X size={12} weight="bold" /></button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{t.name}</div>
                        <div className="flex items-center gap-2 tracker text-[10px] text-zinc-500">
                          {t.unit && <span>{t.unit}</span>}
                          {t.unit && <span>·</span>}
                          <span>{(t.direction || "desc") === "asc" ? "lower = better" : "higher = better"}</span>
                        </div>
                      </div>
                      {!t.active && <span className="tracker text-[10px] text-zinc-500">inactive</span>}
                      <button onClick={() => startEdit(t)} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`metric-template-edit-${t.id}`}><PencilSimple size={12} weight="bold" /></button>
                      <button onClick={() => deleteTemplate(t.id)} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`metric-template-del-${t.id}`}><Trash size={12} weight="bold" /></button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Log + history */}
      {clientId && templates.filter((t) => t.active !== false).length > 0 && (
        <div className="space-y-4" data-testid="custom-metrics-log-area">
          {/* PDF export bar */}
          <div className="flex items-center justify-between">
            <div className="tracker text-[10px] text-zinc-500">[ HISTORY & CHARTS ]</div>
            <button
              onClick={downloadPdf}
              className="btn-ghost !py-1.5 !px-3 !text-[10px]"
              data-testid="assessment-pdf-btn"
              title="Download full assessment history as PDF"
            >
              <FilePdf size={12} weight="bold" /> Export PDF
            </button>
          </div>

          {templates.filter((t) => t.active !== false).map((t) => {
            const rows = entriesByMetric[t.id] || [];
            const direction = (t.direction || "desc").toLowerCase(); // asc=lower is better, desc=higher
            // Sorted oldest→newest for the chart + baseline/latest.
            const chartData = [...rows]
              .filter((e) => typeof e.value === "number" || !Number.isNaN(Number(e.value)))
              .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
              .map((e) => ({
                date: new Date(e.recorded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                value: Number(e.value),
              }));
            const numericValues = chartData.map((d) => d.value);
            const baseline = numericValues.length ? numericValues[0] : null;
            const latest = numericValues.length ? numericValues[numericValues.length - 1] : null;
            const best = numericValues.length
              ? (direction === "asc" ? Math.min(...numericValues) : Math.max(...numericValues))
              : null;
            // Improvement: positive % if latest is better than baseline given direction.
            let improvementPct = null;
            let improved = null;
            if (baseline != null && latest != null && baseline !== 0 && chartData.length >= 2) {
              const rawDelta = latest - baseline;
              improved = direction === "asc" ? rawDelta < 0 : rawDelta > 0;
              improvementPct = Math.abs(100 * rawDelta / baseline);
            }
            const fmt = (v) => `${(+v).toFixed(Math.abs(v) < 10 ? 2 : 1).replace(/\.0+$/, "")}${t.unit ? ` ${t.unit}` : ""}`;
            return (
              <div key={t.id} className="card-flat p-4 sm:p-6" data-testid={`custom-metric-block-${t.id}`}>
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div>
                    <h4 className="font-semibold">{t.name}</h4>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {t.unit && <div className="tracker text-[10px] text-zinc-500">{t.unit}</div>}
                      <div className="tracker text-[10px] text-zinc-600">
                        {direction === "asc" ? "lower = better" : "higher = better"}
                      </div>
                      {best != null && (
                        <div className="tracker text-[10px] text-emerald-400" data-testid={`custom-metric-best-${t.id}`}>
                          Best {fmt(best)}
                        </div>
                      )}
                      {/* Latest is a fresh PR — show a share button */}
                      {latest != null && best != null && latest === best && chartData.length >= 2 && (
                        <button
                          onClick={() => sharePr(t.id)}
                          className="tracker text-[10px] text-brand-light hover:text-white flex items-center gap-1 border border-brand/40 px-2 py-0.5"
                          data-testid={`share-pr-${t.id}`}
                          title="Share this PR"
                        >
                          <ShareNetwork size={11} weight="bold" /> Share PR
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="any"
                      value={logInputs[t.id] ?? ""}
                      onChange={(e) => setLogInputs((p) => ({ ...p, [t.id]: e.target.value }))}
                      placeholder="Value"
                      className="w-28 px-3 py-2 border bg-transparent"
                      data-testid={`custom-metric-value-${t.id}`}
                    />
                    <button onClick={() => logValue(t.id)} className="btn-primary" data-testid={`custom-metric-log-${t.id}`}>Log</button>
                  </div>
                </div>

                {/* Baseline vs Latest — the improvement pill */}
                {baseline != null && latest != null && chartData.length >= 2 && (
                  <div
                    className={`mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border ${
                      improved ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
                    }`}
                    data-testid={`custom-metric-baseline-${t.id}`}
                  >
                    <div className="text-xs">
                      <span className="tracker text-[9px] text-zinc-500 mr-1.5">BASELINE</span>
                      <span className="font-mono text-zinc-300">{fmt(baseline)}</span>
                    </div>
                    <div className="text-zinc-600">→</div>
                    <div className="text-xs">
                      <span className="tracker text-[9px] text-zinc-500 mr-1.5">LATEST</span>
                      <span className="font-mono text-white font-semibold">{fmt(latest)}</span>
                    </div>
                    <div className={`ml-auto tracker text-xs font-bold ${improved ? "text-emerald-400" : "text-red-400"}`}>
                      {improved ? "▲" : "▼"} {improvementPct.toFixed(1)}%
                    </div>
                  </div>
                )}

                {chartData.length >= 2 ? (
                  <div className="h-24 -mx-2 mb-3" data-testid={`custom-metric-chart-${t.id}`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                        <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 9 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis hide domain={["auto", "auto"]} />
                        <Tooltip
                          contentStyle={{ background: "#0A0A0A", border: "1px solid #333", fontSize: 11 }}
                          labelStyle={{ color: "var(--brand-light)" }}
                          formatter={(v) => [`${v}${t.unit ? ` ${t.unit}` : ""}`, "Value"]}
                        />
                        <Line type="monotone" dataKey="value" stroke="var(--brand-light)" strokeWidth={2} dot={{ r: 3, fill: "var(--brand)" }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : chartData.length === 1 ? (
                  <div className="text-[10px] text-zinc-600 italic mb-3 flex items-center gap-1">
                    <TrendUp size={11} weight="bold" /> Log one more entry to unlock the progress chart.
                  </div>
                ) : null}

                {rows.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                    {rows.slice(0, 6).map((e) => (
                      <div key={e.id} className="flex items-center justify-between px-3 py-2 border border-white/5 text-xs" data-testid={`custom-metric-entry-${e.id}`}>
                        <div>
                          <div className="font-mono font-semibold">{e.value}{t.unit ? ` ${t.unit}` : ""}</div>
                          <div className="tracker text-[9px] text-zinc-500">{new Date(e.recorded_at).toLocaleDateString()}</div>
                        </div>
                        <button onClick={() => deleteEntry(e.id)} className="text-zinc-500 hover:text-red-400" title="Delete"><Trash size={12} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                )}

                {canEditTemplates && (
                  <LeaderboardStrip
                    metricId={t.id}
                    unit={t.unit}
                    direction={direction}
                    highlightClientId={clientId}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Collapsible leaderboard — top clients by best value on this test. Trainer-only. */
function LeaderboardStrip({ metricId, unit, direction, highlightClientId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/custom-metrics/${metricId}/leaderboard`);
      setRows(data?.rows || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) load();
  };

  return (
    <div className="mt-4 pt-3 border-t border-white/5" data-testid={`leaderboard-wrap-${metricId}`}>
      <button
        onClick={onToggle}
        className="tracker text-[10px] text-brand-light hover:text-white flex items-center gap-1"
        data-testid={`leaderboard-toggle-${metricId}`}
      >
        {open ? "▾" : "▸"} Roster leaderboard
      </button>
      {open && (
        <div className="mt-3" data-testid={`leaderboard-list-${metricId}`}>
          {loading ? (
            <div className="tracker text-[10px] text-zinc-500 py-2">Loading…</div>
          ) : rows == null || rows.length === 0 ? (
            <div className="text-[10px] text-zinc-600 italic py-2">No results logged yet across your roster.</div>
          ) : (
            <ol className="space-y-1">
              {rows.slice(0, 10).map((r, i) => (
                <li
                  key={r.client_id}
                  className={`flex items-center justify-between px-3 py-1.5 text-xs border ${
                    r.client_id === highlightClientId ? "border-brand bg-brand/10" : "border-white/5"
                  }`}
                  data-testid={`leaderboard-row-${metricId}-${i}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="tracker text-[10px] text-zinc-500 w-5 shrink-0">#{i + 1}</span>
                    <span className="truncate">{r.client_name}</span>
                  </div>
                  <span className="font-mono font-semibold shrink-0">{r.value}{unit ? ` ${unit}` : ""}</span>
                </li>
              ))}
            </ol>
          )}
          {rows && rows.length > 0 && (
            <div className="tracker text-[9px] text-zinc-600 mt-2">
              Ranked by best {direction === "asc" ? "lowest" : "highest"} value ever recorded.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
